const IDENTITY_BASE =
  (import.meta.env.VITE_IDENTITY_BASE_URL as string | undefined)?.replace(/\/$/, "") || "";

export type IdentityErrorBody = {
  error?: string;
  message?: string;
  retry_after_seconds?: number;
};

export type LegalAcceptanceStatus = {
  accepted: boolean;
  terms_version: string;
  privacy_version: string;
  consented_at?: string;
  needs_reaccept: boolean;
  entity: "Speedy Systems Pty Ltd";
};

export type LoginSuccess = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  user_id: string;
  tenant_id: string;
  user?: { id: string; email: string; name?: string };
};

async function parseJson(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return { error: text };
  }
}

export function errorMessage(body: Record<string, unknown>, fallback: string): string {
  const err = body.error;
  if (typeof err === "string" && err.trim()) {
    if (err === "invalid_credentials") return "SpeedyCore Identity: Invalid credentials";
    if (err === "jwt_expired" || err === "token_expired") return "SpeedyCore Identity: Token expired";
    if (err === "session_invalid" || err === "invalid_session") return "SpeedyCore Identity: Session invalid";
    if (err === "client_id_mismatch" || err === "invalid_client") return "SpeedyCore Identity: OAuth client mismatch";
    if (err === "forbidden" || err === "permission_denied") return "SpeedyCore Identity: Permission denied";
    if (err === "tenant_not_found") return "SpeedyCore Identity: Tenant not found";
    if (err === "sso_callback_mismatch" || err === "invalid_redirect_uri") return "SpeedyCore Identity: SSO callback mismatch";
    if (err === "unsupported_grant_type" || err === "unsupported_provider") return "SpeedyCore Identity: Unsupported OAuth provider";
    if (err === "missing_scope" || err === "invalid_scope") return "SpeedyCore Identity: Missing OAuth scope";
    if (err === "verification_required" || err === "email_not_verified") return "Please verify your email to continue";
    if (err === "verification_expired") return "Verification link expired — request a new one";
    if (err === "rate_limit_exceeded") {
      if (typeof body.message === "string" && body.message.trim()) return body.message;
      const retryAfter =
        typeof body.retry_after_seconds === "number" ? body.retry_after_seconds : 15 * 60;
      const minutes = Math.max(1, Math.ceil(retryAfter / 60));
      return `Too many sign-in attempts. Try again in about ${minutes} minute${minutes === 1 ? "" : "s"}.`;
    }
    if (err === "disposable_email_blocked") {
      return "Please use a real email address (Gmail, Outlook, work email, etc.). Temporary or disposable addresses are not allowed.";
    }
    if (err === "invalid_email") {
      return "Please enter a valid email address.";
    }
    if (err === "ACCOUNT_ALREADY_EXISTS" || err === "email_taken") {
      return "Email already in use";
    }
    if (err === "ai_acknowledgement_required") {
      return "Please tick the AI responsibility acknowledgement to continue.";
    }
    if (err === "legal_consent_required") {
      return "You must accept the Terms & Conditions and Privacy Policy to continue.";
    }
    return err.replace(/_/g, " ");
  }
  if (typeof body.message === "string" && body.message.trim()) return body.message;
  return fallback;
}

/** IDE gets longer-lived access tokens (JWT_EXPIRES_IN_IDE). */
export function oauthClientIdForRedirect(redirectUri: string | null | undefined): "ide" | "identity_web" {
  if (!redirectUri) return "identity_web";
  try {
    const host = new URL(redirectUri).hostname.toLowerCase();
    if (host === "ide.speedycore.com.au" || host.startsWith("ide.")) return "ide";
    if (host === "pluginfactoryai.com" || host.endsWith(".pluginfactoryai.com")) return "ide";
  } catch {
    /* ignore */
  }
  return /ide\.speedycore\.com\.au/i.test(redirectUri) ||
    /pluginfactoryai\.com/i.test(redirectUri)
    ? "ide"
    : "identity_web";
}

export async function identityLogin(
  email: string,
  password: string,
  clientId: string = "identity_web",
): Promise<LoginSuccess> {
  const res = await fetch(`${IDENTITY_BASE}/identity/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password, client_id: clientId }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(errorMessage(body, "Sign in failed."));
  }
  if (body.legal_acceptance_required === true) {
    const err = new Error("LEGAL_ACCEPTANCE_REQUIRED");
    (err as Error & { code?: string }).code = "LEGAL_ACCEPTANCE_REQUIRED";
    throw err;
  }
  return body as unknown as LoginSuccess;
}

export type SignupSuccess = LoginSuccess;

export async function identityRegister(
  email: string,
  password: string,
  name: string | undefined,
  clientId: string = "identity_web",
  legalConsent: boolean = false,
  termsVersion?: string,
  privacyVersion?: string,
  aiAcknowledged: boolean = false,
): Promise<SignupSuccess> {
  const res = await fetch(`${IDENTITY_BASE}/identity/register`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email,
      password,
      name: name || undefined,
      client_id: clientId,
      legal_consent: legalConsent,
      ai_acknowledged: aiAcknowledged,
      terms_version: termsVersion,
      privacy_version: privacyVersion,
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const code = typeof body.error === "string" ? body.error : "";
    if (
      code === "ACCOUNT_ALREADY_EXISTS" ||
      code === "email_already_registered" ||
      code === "email_taken"
    ) {
      const err = new Error("You already have a SpeedyCore account. Log in instead.");
      (err as Error & { code?: string }).code = "ACCOUNT_ALREADY_EXISTS";
      throw err;
    }
    throw new Error(errorMessage(body, "Sign up failed."));
  }
  return body as unknown as SignupSuccess;
}

export async function getLegalAcceptance(
  userIdOrBearer?: string,
): Promise<LegalAcceptanceStatus> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (userIdOrBearer) {
    headers.Authorization = `Bearer ${userIdOrBearer}`;
  }
  const res = await fetch(`${IDENTITY_BASE}/identity/legal/status`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not load legal acceptance status."));
  return body as unknown as LegalAcceptanceStatus;
}

export async function requireLegalAcceptance(): Promise<{
  redirect_url: string;
  required: boolean;
}> {
  const res = await fetch(`${IDENTITY_BASE}/identity/legal/require`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not check legal acceptance requirement."));
  return body as unknown as { redirect_url: string; required: boolean };
}

export async function getCurrentLegalVersion(): Promise<{
  terms_version: string;
  privacy_version: string;
  entity: string;
}> {
  const res = await fetch(`${IDENTITY_BASE}/identity/legal/version`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not load current legal versions."));
  return body as unknown as { terms_version: string; privacy_version: string; entity: string };
}

export async function acceptLegalTerms(params: {
  terms_version: string;
  privacy_version: string;
  ai_acknowledged?: boolean;
}): Promise<LegalAcceptanceStatus> {
  const res = await fetch(`${IDENTITY_BASE}/identity/legal/accept`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      terms_version: params.terms_version,
      privacy_version: params.privacy_version,
      ai_acknowledged: params.ai_acknowledged === true,
      accept: true,
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const code = typeof body.error === "string" ? body.error : "";
    const err = new Error(errorMessage(body, "Could not accept legal terms."));
    if (code === "version_required" || res.status === 402) {
      (err as Error & { code?: string }).code = "VERSION_REQUIRED";
    }
    throw err;
  }
  return body as unknown as LegalAcceptanceStatus;
}

export async function identityForgotPassword(email: string): Promise<{ ok?: boolean }> {
  const res = await fetch(`${IDENTITY_BASE}/identity/forgot-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not start password reset."));
  return body as { ok?: boolean };
}

export async function identityResetPassword(token: string, password: string): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/reset-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not reset password."));
}

export async function identityVerifyEmail(token: string): Promise<{ email?: string }> {
  const res = await fetch(`${IDENTITY_BASE}/identity/verify-email`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ token }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Email verification failed."));
  return body as { email?: string };
}

/** Best-effort server session revoke (same-origin on auth.speedycore.com.au). */
export async function identityLogout(): Promise<void> {
  try {
    await fetch(`${IDENTITY_BASE}/identity/logout`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    /* ignore */
  }
}

export function safeRedirectUri(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    const host = url.hostname.toLowerCase();
    const allowed =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".speedycore.com.au") ||
      host === "speedycore.com.au" ||
      host.endsWith(".constructiveos.com.au") ||
      host === "constructiveos.com.au" ||
      host.endsWith(".creatorshub.com.au") ||
      host === "creatorshub.com.au" ||
      host.endsWith(".hubcircles.com.au") ||
      host === "hubcircles.com.au" ||
      host.endsWith(".adverthub.com.au") ||
      host === "adverthub.com.au" ||
      host.endsWith(".hubjobs.com.au") ||
      host === "hubjobs.com.au" ||
      host.endsWith(".buildersbrochures.com.au") ||
      host === "buildersbrochures.com.au" ||
      host.endsWith(".mytradieweb.com.au") ||
      host === "mytradieweb.com.au" ||
      host.endsWith(".pluginfactoryai.com") ||
      host === "pluginfactoryai.com";
    if (!allowed) return null;
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** Hand tokens to the return app via URL hash (never sent to the server). */
export function redirectAfterAuth(
  redirectUri: string | null,
  tokens?: Pick<
    LoginSuccess,
    "access_token" | "refresh_token" | "expires_in" | "tenant_id" | "user_id" | "user"
  >,
): void {
  const target =
    safeRedirectUri(redirectUri) ||
    (typeof window !== "undefined"
      ? `${window.location.protocol}//${window.location.host.replace(/^auth\./, "")}/`
      : "https://speedycore.com.au/");
  if (tokens?.access_token) {
    const url = new URL(target);
    const frag = new URLSearchParams();
    frag.set("access_token", tokens.access_token);
    if (typeof tokens.refresh_token === "string") frag.set("refresh_token", tokens.refresh_token);
    if (tokens.expires_in != null) frag.set("expires_in", String(tokens.expires_in));
    if (typeof tokens.tenant_id === "string" && tokens.tenant_id.trim()) {
      frag.set("tenant_id", tokens.tenant_id);
    }
    if (typeof tokens.user_id === "string" && tokens.user_id.trim()) {
      frag.set("user_id", tokens.user_id);
    }
    if (tokens.user) {
      if (typeof tokens.user.email === "string" && tokens.user.email.trim()) {
        frag.set("email", tokens.user.email);
      }
      if (typeof tokens.user.id === "string" && tokens.user.id.trim()) {
        frag.set("uid", tokens.user.id);
      }
      if (typeof tokens.user.name === "string" && tokens.user.name.trim()) {
        frag.set("name", tokens.user.name);
      }
    }
    url.hash = frag.toString();
    window.location.assign(url.toString());
    return;
  }
  window.location.assign(target);
}

export async function redirectAfterAuthWithHandoff(
  redirectUri: string | null,
  clientId: "ide" | "identity_web" = "identity_web",
): Promise<void> {
  const tokens = await identitySessionHandoff(clientId).catch(() => null);
  redirectAfterAuth(redirectUri, tokens ?? undefined);
}

/** Mint SPA tokens from an existing HttpOnly SpeedyCore session cookie. */
export async function identitySessionHandoff(
  clientId: "ide" | "identity_web" = "identity_web",
): Promise<LoginSuccess | null> {
  const qs = clientId === "ide" ? "?client_id=ide" : "";
  const res = await fetch(`${IDENTITY_BASE}/identity/session/handoff${qs}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  const body = await parseJson(res);
  if (typeof body.access_token !== "string") return null;
  return body as unknown as LoginSuccess;
}

export async function identityRefreshToken(
  refreshToken: string,
  clientId: string = "identity_web",
): Promise<LoginSuccess> {
  const res = await fetch(`${IDENTITY_BASE}/identity/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken, client_id: clientId }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(errorMessage(body, "Refresh failed."));
  }
  return body as unknown as LoginSuccess;
}

export async function identityRequestVerifyEmail(email: string): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/verify-email/request`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not send verification email."));
}

export type IdentitySessionsResponse = {
  sessions: Array<{
    id: string;
    ip_masked: string;
    device: string;
    created_at: string;
    expires_at: string;
    current?: boolean;
  }>;
};

export async function identityGetSessions(
  accessToken: string,
): Promise<IdentitySessionsResponse> {
  const res = await fetch(`${IDENTITY_BASE}/identity/settings/sessions`, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not load sessions."));
  return body as IdentitySessionsResponse;
}

export async function identityChangePassword(
  accessToken: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/settings/change-password`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not change password."));
}

export async function identityChangeEmail(
  accessToken: string,
  email: string,
): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/settings/change-email`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ email }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not change email."));
}

export async function identityLogoutAllSessions(accessToken: string): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/settings/sessions/logout-all`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const body = await parseJson(res);
    throw new Error(errorMessage(body, "Could not logout sessions."));
  }
}

export async function identityDeleteAccount(accessToken: string): Promise<void> {
  const res = await fetch(`${IDENTITY_BASE}/identity/account/delete`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const body = await parseJson(res);
    throw new Error(errorMessage(body, "Could not delete account."));
  }
}

export type OAuthClientInfo = {
  name: string;
  domain: string;
  scopes: string[];
  is_first_party?: boolean;
};

export async function identityGetOAuthClient(clientId: string): Promise<OAuthClientInfo> {
  const url = new URL(`${IDENTITY_BASE}/identity/oauth/client`);
  url.searchParams.set("client_id", clientId);
  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "App not found."));
  return body as OAuthClientInfo;
}

export async function identityOAuthApprove(params: {
  accessToken: string;
  clientId: string;
  redirectUri: string;
  state?: string;
  scopes?: string[];
  denied?: boolean;
}): Promise<{ redirect_to: string }> {
  const res = await fetch(`${IDENTITY_BASE}/identity/oauth/approve`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify({
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      state: params.state,
      scopes: params.scopes ?? [],
      denied: params.denied === true,
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) throw new Error(errorMessage(body, "Could not approve app."));
  return body as { redirect_to: string };
}

const TELEMETRY_EVENTS = new Set([
  "identity.login_failed",
  "identity.signup_failed",
  "identity.sso_callback_error",
  "identity.token_expired",
  "identity.permission_denied",
]);

export function currentHubOrigin(): string {
  try {
    return window.location.hostname || "";
  } catch {
    return "";
  }
}

export async function sendIdentityTelemetry(
  eventName: string,
  meta?: Partial<{
    error_code: string;
    hub_origin: string;
    device: string;
    ip: string;
    user_agent: string;
  }>,
): Promise<void> {
  if (!TELEMETRY_EVENTS.has(eventName)) return;
  try {
    await fetch(`${IDENTITY_BASE}/identity/telemetry/event`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: eventName,
        meta: {
          hub_origin: currentHubOrigin(),
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          ...(meta ?? {}),
        },
      }),
    });
  } catch {
    /* telemetry is best-effort */
  }
}
